import { apiClient } from '@shared/api/client';
import { ApiResponse } from '@shared/api/api.types';
import {
  SurveyAuditDTO,
  UpdateSurveyAuditDTO,
  UpdateQuestionInclusionsDTO,
  UpdateCommentInclusionsDTO,
  BulkUpdateInclusionsDTO,
  AuditProgressResponseDTO,
  AuditTrailResponseDTO,
  AuditStatisticsResponseDTO,
  SurveyAuditListDTO,
  AuditPermissionDTO,
  CompleteAuditDTO,
  PublishAuditDTO,
} from '../dtos';

// Survey Audit API
export const surveyAuditApi = {
  // Get survey audit by ID
  async getSurveyAudit(auditId: string): Promise<ApiResponse<SurveyAuditDTO>> {
    return apiClient.get<SurveyAuditDTO>(`/survey-audits/${auditId}`);
  },

  // Get survey audits by survey instance
  async getSurveyAuditsBySurveyInstance(
    surveyInstanceId: string
  ): Promise<ApiResponse<SurveyAuditListDTO>> {
    return apiClient.get<SurveyAuditListDTO>(`/survey-instances/${surveyInstanceId}/audits`);
  },

  // Get survey audits by subject with optional status filter
  async getSurveyAuditsBySubject(
    subjectId: string,
    options?: {
      status?: string | string[];
      limit?: number;
    }
  ): Promise<ApiResponse<SurveyAuditListDTO>> {
    const params = new URLSearchParams();
    if (options?.status) {
      if (Array.isArray(options.status)) {
        // Send array as comma-separated string
        params.append('status', options.status.join(','));
      } else {
        params.append('status', options.status);
      }
    }
    if (options?.limit) {
      params.append('limit', options.limit.toString());
    }
    const queryString = params.toString();
    const url = queryString
      ? `/subjects/${subjectId}/audits?${queryString}`
      : `/subjects/${subjectId}/audits`;
    return apiClient.get<SurveyAuditListDTO>(url);
  },

  // Get survey audits by tenant
  async getSurveyAuditsByTenant(tenantId: string): Promise<ApiResponse<SurveyAuditListDTO>> {
    return apiClient.get<SurveyAuditListDTO>(`/tenants/${tenantId}/audits`);
  },

  // Start new audit (create)
  async startAudit(
    surveyInstanceId: string,
    userRole: 'PROFESSOR' | 'COURSE_REPRESENTATIVE',
    surveyId: string
  ): Promise<ApiResponse<SurveyAuditDTO>> {
    return apiClient.post<SurveyAuditDTO>(`/survey-instances/${surveyInstanceId}/audit`, {
      instanceId: surveyInstanceId,
      userRole: userRole === 'COURSE_REPRESENTATIVE' ? 'COURSE_REPRESENTATIVE' : userRole,
      surveyId,
    });
  },

  // Update survey audit
  async updateSurveyAudit(
    auditId: string,
    data: UpdateSurveyAuditDTO
  ): Promise<ApiResponse<SurveyAuditDTO>> {
    return apiClient.put<SurveyAuditDTO>(`/survey-audits/${auditId}`, data);
  },

  // Update question response inclusions
  async updateQuestionInclusions(
    auditId: string,
    questionId: string,
    data: UpdateQuestionInclusionsDTO
  ): Promise<ApiResponse<SurveyAuditDTO>> {
    return apiClient.put<SurveyAuditDTO>(
      `/survey-audits/${auditId}/questions/${questionId}/responses`,
      data
    );
  },

  // Update question comment inclusions
  async updateCommentInclusions(
    auditId: string,
    questionId: string,
    data: UpdateCommentInclusionsDTO
  ): Promise<ApiResponse<SurveyAuditDTO>> {
    return apiClient.put<SurveyAuditDTO>(
      `/survey-audits/${auditId}/questions/${questionId}/comments`,
      data
    );
  },

  // Bulk update inclusions
  async bulkUpdateInclusions(
    auditId: string,
    data: BulkUpdateInclusionsDTO
  ): Promise<ApiResponse<SurveyAuditDTO>> {
    return apiClient.put<SurveyAuditDTO>(`/survey-audits/${auditId}/bulk-update`, data);
  },

  // Complete audit
  async completeAudit(auditId: string): Promise<ApiResponse<CompleteAuditDTO>> {
    return apiClient.post<CompleteAuditDTO>(`/survey-audits/${auditId}/complete`);
  },

  // Publish audit
  async publishAudit(auditId: string): Promise<ApiResponse<PublishAuditDTO>> {
    return apiClient.post<PublishAuditDTO>(`/survey-audits/${auditId}/publish`);
  },

  // Archive audit
  async archiveAudit(auditId: string, subjectId: string): Promise<ApiResponse<SurveyAuditDTO>> {
    return apiClient.post<SurveyAuditDTO>(
      `/survey-audits/${auditId}/archive?subjectId=${subjectId}`
    );
  },

  // Unarchive audit
  async unarchiveAudit(auditId: string, subjectId: string): Promise<ApiResponse<SurveyAuditDTO>> {
    return apiClient.post<SurveyAuditDTO>(
      `/survey-audits/${auditId}/unarchive?subjectId=${subjectId}`
    );
  },

  // Get audit progress
  async getAuditProgress(auditId: string): Promise<ApiResponse<AuditProgressResponseDTO>> {
    return apiClient.get<AuditProgressResponseDTO>(`/survey-audits/${auditId}/progress`);
  },

  // Get audit trail
  async getAuditTrail(auditId: string): Promise<ApiResponse<AuditTrailResponseDTO>> {
    return apiClient.get<AuditTrailResponseDTO>(`/survey-audits/${auditId}/trail`);
  },

  // Get audit statistics
  async getAuditStatistics(auditId: string): Promise<ApiResponse<AuditStatisticsResponseDTO>> {
    return apiClient.get<AuditStatisticsResponseDTO>(`/survey-audits/${auditId}/statistics`);
  },

  // Check audit permissions
  async checkAuditPermissions(
    userId: string,
    subjectId: string
  ): Promise<ApiResponse<AuditPermissionDTO>> {
    return apiClient.get<AuditPermissionDTO>(
      `/users/${userId}/subjects/${subjectId}/audit-permissions`
    );
  },

  // Delete survey audit
  async deleteSurveyAudit(auditId: string): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/survey-audits/${auditId}`);
  },

  // Get questions with responses for audit
  async getAuditQuestions(auditId: string): Promise<ApiResponse<any[]>> {
    return apiClient.get<any[]>(`/survey-audits/${auditId}/questions`);
  },

  // Get survey responses for specific question in audit
  async getQuestionResponses(auditId: string, questionId: string): Promise<ApiResponse<any[]>> {
    return apiClient.get<any[]>(`/survey-audits/${auditId}/questions/${questionId}/responses`);
  },
};
